// components/common/MobileSelect.js
import React, { useState, useEffect } from 'react';
import { X, Check, ChevronDown } from 'lucide-react';

/**
 * MobileSelect - A reusable dropdown component that uses a beautiful full-screen modal
 * interface on all screen sizes
 *
 * @param {string} label - Label for the select field
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when selection changes (receives value)
 * @param {Array} options - Array of options. Can be strings or objects with {value, label, description, icon}
 * @param {string} placeholder - Placeholder text when no value is selected
 * @param {string} error - Error message to display
 * @param {boolean} required - Whether field is required
 * @param {boolean} disabled - Whether field is disabled
 * @param {string} className - Additional classes for the container
 */
export function MobileSelect({
  label,
  value = '',
  onChange,
  options = [],
  placeholder = 'בחר אפשרות',
  error,
  required = false,
  disabled = false,
  className = '',
  searchable = false,
  renderOption,
  renderSelected,
  emptyMessage = 'אין אפשרויות זמינות',
  multiSelect = false
}) {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Detect if we're on mobile for layout purposes
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  // Normalize options to objects
  const normalizedOptions = options.map(opt => {
    if (typeof opt === 'string') {
      return { value: opt, label: opt };
    }
    return opt;
  });

  // Filter options based on search
  const filteredOptions = searchable && searchTerm
    ? normalizedOptions.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : normalizedOptions;

  // Normalize value to array for multi-select
  const selectedValues = multiSelect ? (Array.isArray(value) ? value : []) : [value];

  // Get selected option(s)
  const selectedOption = normalizedOptions.find(opt => opt.value === value);
  const selectedOptions = normalizedOptions.filter(opt => selectedValues.includes(opt.value));

  // Handle selection
  const handleSelect = (optionValue) => {
    if (multiSelect) {
      // Toggle selection for multi-select
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange?.(newValues);
    } else {
      // Single select
      onChange?.(optionValue);
      setShowModal(false);
      setSearchTerm('');
    }
  };

  // Handle click on trigger
  const handleTriggerClick = () => {
    if (!disabled) {
      setShowModal(true);
    }
  };

  // Render the display value
  const renderDisplayValue = () => {
    if (multiSelect) {
      if (selectedOptions.length === 0) return placeholder;
      // Show comma-separated list of service names
      return selectedOptions.map(opt => opt.label).join(', ');
    }

    if (renderSelected && selectedOption) {
      return renderSelected(selectedOption);
    }
    return selectedOption ? selectedOption.label : placeholder;
  };

  return (
    <>
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 mr-1">*</span>}
          </label>
        )}

        {/* Trigger button */}
        <button
          type="button"
          onClick={handleTriggerClick}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white text-right flex items-center justify-between ${
            error
              ? 'border-red-300 text-red-900'
              : 'border-gray-300 text-gray-900'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'hover:bg-gray-50 active:bg-gray-50'}`}
        >
          <span className={selectedOption || selectedOptions.length > 0 ? 'text-gray-900' : 'text-gray-400'}>
            {renderDisplayValue()}
          </span>
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mr-2" />
        </button>

        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className={`bg-white flex flex-col shadow-xl ${
            isMobile
              ? 'w-full h-full'
              : 'rounded-lg max-w-lg w-full max-h-[80vh]'
          }`}>
            {/* Header */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {label || placeholder}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSearchTerm('');
                }}
                className="p-2 -m-2 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Search input (if searchable) */}
            {searchable && (
              <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-b border-gray-200">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="חפש..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none"
                  autoFocus
                />
              </div>
            )}

            {/* Options list */}
            <div className="flex-1 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="flex items-center justify-center h-full px-4 py-12">
                  <p className="text-gray-500 text-center">{emptyMessage}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredOptions.map((option) => {
                    const isSelected = multiSelect
                      ? selectedValues.includes(option.value)
                      : option.value === value;
                    const isDisabled = option.disabled;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => !isDisabled && handleSelect(option.value)}
                        disabled={isDisabled}
                        className={`w-full px-4 py-4 flex items-center justify-between text-right transition-colors ${
                          isDisabled
                            ? 'opacity-50 cursor-not-allowed bg-gray-50'
                            : isSelected
                            ? 'bg-pink-50'
                            : 'hover:bg-gray-50 active:bg-gray-50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          {renderOption ? (
                            renderOption(option, isSelected)
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                {option.icon && (
                                  <div className="flex-shrink-0 text-2xl">
                                    {option.icon}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-base font-medium ${
                                    isSelected ? 'text-pink-700' : 'text-gray-900'
                                  }`}>
                                    {option.label}
                                  </p>
                                  {option.description && (
                                    <p className="text-sm text-gray-500 mt-0.5">
                                      {option.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {isSelected && (
                          <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mr-3" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setSearchTerm('');
                }}
                className="w-full px-4 py-3 bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 active:bg-pink-600 transition-colors"
              >
                {multiSelect && selectedValues.length > 0
                  ? `בחר (${selectedValues.length})`
                  : 'סגור'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
